import { useEffect, useMemo, useState } from 'react';
import { getAdminUsers, revokeAdminUserSessions, updateAdminUser } from '../api/auth';

const ROLES = ['viewer', 'fleet_manager', 'admin'];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setError('');
    setLoading(true);
    try {
      const data = await getAdminUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }

  function updateLocal(userId, field, value) {
    setUsers(current => current.map(user => user.id === userId ? { ...user, [field]: value } : user));
  }

  async function saveUser(user) {
    setSavingId(user.id);
    setError('');
    try {
      const payload = {
        full_name: user.full_name || null,
        organization: user.organization || null,
        role: user.role,
        active: Boolean(user.active),
        access_expires_at: user.access_expires_at || null,
      };
      const data = await updateAdminUser(user.id, payload);
      setUsers(current => current.map(item => item.id === user.id ? data.user : item));
    } catch (err) {
      setError(err.message || 'Could not save user.');
    } finally {
      setSavingId('');
    }
  }

  async function approveUser(user) {
    await saveUser({ ...user, active: true, role: user.role || 'viewer' });
  }

  async function revokeSessions(userId) {
    setSavingId(userId);
    setError('');
    try {
      await revokeAdminUserSessions(userId);
    } catch (err) {
      setError(err.message || 'Could not revoke sessions.');
    } finally {
      setSavingId('');
    }
  }

  const summary = useMemo(() => {
    const active = users.filter(user => user.active).length;
    const pending = users.filter(user => !user.active).length;
    const admins = users.filter(user => user.role === 'admin').length;
    const expired = users.filter(user => isExpired(user.access_expires_at)).length;
    return { active, pending, admins, expired };
  }, [users]);

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div>
          <div className="admin-users__kicker">OWNER CONTROL</div>
          <h1>User Management</h1>
        </div>
        <button type="button" onClick={loadUsers} disabled={loading}>
          REFRESH
        </button>
      </div>

      <div className="admin-users__stats">
        <Metric label="Users" value={users.length} />
        <Metric label="Active" value={summary.active} />
        <Metric label="Pending" value={summary.pending} tone={summary.pending ? 'warn' : ''} />
        <Metric label="Admins" value={summary.admins} />
        <Metric label="Expired" value={summary.expired} tone={summary.expired ? 'warn' : ''} />
      </div>

      {error && <div className="admin-users__error" role="alert">{error}</div>}
      {loading ? (
        <div className="admin-users__empty">LOADING USERS...</div>
      ) : (
        <div className="admin-users__table-wrap">
          <table className="admin-users__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Organisation</th>
                <th>Role</th>
                <th>Status</th>
                <th>Access expires</th>
                <th>Last login</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className={`${isExpired(user.access_expires_at) ? 'is-expired' : ''} ${!user.active ? 'is-pending' : ''}`}>
                  <td>
                    <input
                      value={user.full_name || ''}
                      onChange={e => updateLocal(user.id, 'full_name', e.target.value)}
                      placeholder="Not set"
                    />
                  </td>
                  <td>
                    <div className="admin-users__email" title={user.id}>{user.email}</div>
                  </td>
                  <td>
                    <input
                      value={user.organization || ''}
                      onChange={e => updateLocal(user.id, 'organization', e.target.value)}
                      placeholder="Not set"
                    />
                  </td>
                  <td>
                    <select value={user.role} onChange={e => updateLocal(user.id, 'role', e.target.value)}>
                      {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={user.active ? 'active' : 'disabled'} onChange={e => updateLocal(user.id, 'active', e.target.value === 'active')}>
                      <option value="active">active</option>
                      <option value="disabled">pending / disabled</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="datetime-local"
                      value={toLocalInput(user.access_expires_at)}
                      onChange={e => updateLocal(user.id, 'access_expires_at', fromLocalInput(e.target.value))}
                    />
                  </td>
                  <td>{formatDate(user.last_login_at) || 'Never'}</td>
                  <td>
                    <div className="admin-users__actions">
                      {!user.active && (
                        <button type="button" onClick={() => approveUser(user)} disabled={savingId === user.id}>
                          APPROVE
                        </button>
                      )}
                      <button type="button" onClick={() => saveUser(user)} disabled={savingId === user.id}>
                        SAVE
                      </button>
                      <button type="button" onClick={() => updateLocal(user.id, 'access_expires_at', null)} disabled={savingId === user.id}>
                        CLEAR
                      </button>
                      <button type="button" onClick={() => revokeSessions(user.id)} disabled={savingId === user.id}>
                        REVOKE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className={`admin-users__metric ${tone ? `admin-users__metric--${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function isExpired(value) {
  return value ? new Date(value).getTime() <= Date.now() : false;
}

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function toLocalInput(value) {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function fromLocalInput(value) {
  return value ? new Date(value).toISOString() : null;
}
